import { CommonModule, DOCUMENT, ViewportScroller } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';

import { RouterModule } from '@angular/router';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { ScrollspyNavItem } from './entities/scrollspy-nav-item.entity';

export * from './entities/scrollspy-nav-item.entity';

/** @dynamic */
@Component({
    standalone: true,
    selector: 'sh-scrollspy-nav',
    templateUrl: './scrollspy-nav.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, RouterModule]
})
export class ShScrollspyNavComponent extends BaseContainer implements OnInit, OnDestroy, AfterViewInit {
    private readonly ADDED_SCROLL_OFFSET: number = 16;
    private readonly INTERSECTION_THRESHOLD: number = 0.1;

    private readonly offsetContainer$: BehaviorSubject<HTMLElement | null> = new BehaviorSubject<HTMLElement | null>(
        null
    );

    private readonly items$: BehaviorSubject<ScrollspyNavItem[]> = new BehaviorSubject<ScrollspyNavItem[]>([]);
    private readonly scrollContainer$: BehaviorSubject<HTMLElement | Window> = new BehaviorSubject<
        HTMLElement | Window
    >(window);

    private readonly currentObserverItems$: BehaviorSubject<ScrollspyNavItem[]> = new BehaviorSubject<
        ScrollspyNavItem[]
    >([]);

    private currentObserver!: IntersectionObserver;
    private currentOffset = 0;

    public scrollspyNavItems$!: Observable<ScrollspyNavItem[]>;

    @Input() set items(items: ScrollspyNavItem[]) {
        this.items$.next(items || []);
    }

    @Input() set scrollContainer(container: HTMLElement | null) {
        this.scrollContainer$.next(container || window);
    }

    @Input() set offsetContainer(offsetContainer: HTMLElement | null) {
        this.offsetContainer$.next(offsetContainer || null);
    }

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly viewportScroller: ViewportScroller
    ) {
        super();
    }

    ngOnInit(): void {
        this.initScrollObserverItems();
        this.initScrollspyNavItems();
    }

    ngAfterViewInit(): void {
        if (this.isIntersectionObserverSupported()) {
            this.initScrollObserver();
        }
    }

    ngOnDestroy(): void {
        this.disconnectCurrentObserver();

        this.destroy$.next();
        this.destroy$.complete();
    }

    public scrollToTarget(event: Event, target: string): void {
        event.preventDefault();

        const targetSection: HTMLElement | null = this.document.getElementById(target);
        const container: HTMLElement | Window = this.scrollContainer$.value || window;

        if (targetSection) {
            container.scrollTo({
                top: targetSection.offsetTop - (this.currentOffset + this.ADDED_SCROLL_OFFSET),
                behavior: 'smooth'
            });
        }
    }

    private initScrollObserverItems(): void {
        this.items$.pipe(takeUntil(this.destroy$)).subscribe((items: ScrollspyNavItem[]) => {
            this.currentObserverItems$.next(items);
        });
    }

    private initScrollspyNavItems(): void {
        this.scrollspyNavItems$ = this.currentObserverItems$.pipe(
            takeUntil(this.destroy$),
            map((items: ScrollspyNavItem[]) =>
                items.map(item => ({ ...item, firstActive: this.isFirstActiveScrollspyNavItem(items, item) }))
            ),
            shareReplay(1)
        );
    }

    private isFirstActiveScrollspyNavItem(
        allScrollspyNavItems: ScrollspyNavItem[],
        scrollspyNavItem: ScrollspyNavItem
    ): boolean {
        const itemToCheckIndex = allScrollspyNavItems.findIndex(item => item.target === scrollspyNavItem.target);
        const firstActiveItemIndex = allScrollspyNavItems.findIndex(item => item.active);

        return itemToCheckIndex === firstActiveItemIndex;
    }

    private initScrollObserver(): void {
        combineLatest([this.items$, this.scrollContainer$, this.offsetContainer$])
            .pipe(
                map(() => this.setupScrollOffset()),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.setupObserver();
                this.startObserveSections();
            });
    }

    private setupObserver(): void {
        this.disconnectCurrentObserver();

        const currentScrollContainerValue: HTMLElement | Window = this.scrollContainer$.value;
        const root: Element | null =
            currentScrollContainerValue === window ? null : (currentScrollContainerValue as HTMLElement);

        const intersectionObserverOptions: IntersectionObserverInit = {
            root,
            rootMargin: `-${this.currentOffset}px`,
            threshold: this.INTERSECTION_THRESHOLD
        };

        this.currentObserver = new IntersectionObserver(
            this.handleIntersection.bind(this),
            intersectionObserverOptions
        );
    }

    private disconnectCurrentObserver(): void {
        if (this.currentObserver) {
            this.currentObserver.disconnect();
        }
    }

    private setupScrollOffset(): void {
        const offsetContainer: HTMLElement | null = this.offsetContainer$.value;

        this.currentOffset = 0;
        let retries: number = 0;
        // Retry setting the offset every 50ms until it's > 0 or 10 attempts have been made
        const retryInterval = setInterval(() => {
            if (offsetContainer && offsetContainer.offsetHeight > 0) {
                this.currentOffset = offsetContainer.offsetHeight + this.ADDED_SCROLL_OFFSET;
                clearInterval(retryInterval);
                return;
            }
            if (retries >= 10) {
                clearInterval(retryInterval);
                return;
            }

            retries += 1;
        }, 50);

        this.setViewportScrollerOffset();
    }

    private setViewportScrollerOffset(): void {
        this.viewportScroller.setOffset([0, this.currentOffset]);
    }

    private startObserveSections(): void {
        let sectionsToObserve: HTMLElement[] = [];

        let retries: number = 0;
        // Retry setting the offset every 50ms until it's > 0 or 10 attempts have been made
        const retryInterval = setInterval(() => {
            if (sectionsToObserve.length === this.items$.value.length) {
                for (const section of sectionsToObserve) {
                    this.currentObserver.observe(section);
                }
                clearInterval(retryInterval);
                return;
            }
            if (retries >= 10) {
                console.warn('Scrollspy could not find some sections');
                clearInterval(retryInterval);
                return;
            }
            sectionsToObserve = this.items$.value
                .map((item: ScrollspyNavItem) => this.document.getElementById(item.target))
                .filter((item: HTMLElement | null): item is HTMLElement => !!item);
            retries += 1;
        }, 50);
    }

    private handleIntersection(entries: IntersectionObserverEntry[], observer: IntersectionObserver): void {
        this.removeFocusFromItems();

        for (const entry of entries) {
            this.updateSection(entry);
        }
    }

    private updateSection(entry: IntersectionObserverEntry): void {
        const updatedObservers: ScrollspyNavItem[] = [...this.currentObserverItems$.value];

        const observerIndexToUpdate = updatedObservers.findIndex(item => item.target === entry.target.id);
        updatedObservers[observerIndexToUpdate].active =
            entry.isIntersecting && entry.intersectionRatio > this.INTERSECTION_THRESHOLD;

        this.currentObserverItems$.next(updatedObservers);
    }

    private removeFocusFromItems(): void {
        const activeElement: HTMLElement | null = this.document.activeElement as HTMLElement;

        if (activeElement && activeElement.classList.contains('sh-tab-navigation__item')) {
            activeElement.blur();
        }
    }

    private isIntersectionObserverSupported(): boolean {
        return (
            'IntersectionObserver' in window &&
            'IntersectionObserverEntry' in window &&
            'intersectionRatio' in window.IntersectionObserverEntry.prototype
        );
    }
}
